import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { DocumentRequirement, User, UserRole, UploadedDocument } from '../types';
import Modal from '../components/Modal';
import ToggleSwitch from '../components/ToggleSwitch';
import { useAuth } from '../context/AuthContext';

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function triggerDownload(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

const DocumentRequirementsPage = () => {
  const { user } = useAuth();
  const isViewer = user?.role === UserRole.VIEWER;
  const [requirements, setRequirements] = useState<DocumentRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newType, setNewType] = useState('');
  // Starts on Required — the stricter choice, and what every requirement
  // was before optional ones existed.
  const [newIsRequired, setNewIsRequired] = useState(true);
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingRequirement, setDeletingRequirement] = useState<DocumentRequirement | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [applicants, setApplicants] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDocuments, setUserDocuments] = useState<UploadedDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    apiService
      .getDocumentRequirements()
      .then(setRequirements)
      .catch((err) => setError(err.message || 'Failed to load document requirements'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Viewer has no access to the user directory at all (not even to search
    // students here), so skip the fetch entirely rather than let it 403.
    if (isViewer) return;
    apiService
      .getUsers()
      .then((users) => setApplicants(users.filter((u) => u.role === UserRole.APPLICANT)))
      .catch(() => {
        // Non-fatal — the applicant search just won't have anyone to find.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openUser = async (user: User) => {
    setSelectedUser(user);
    setUserDocuments([]);
    setDocumentsError('');
    setDocumentsLoading(true);
    try {
      const docs = await apiService.getUserProfileDocuments(user.id);
      setUserDocuments(docs);
    } catch (err: any) {
      setDocumentsError(err.message || 'Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleViewDocument = async (documentId: number) => {
    setViewingId(documentId);
    setDocumentsError('');
    try {
      const { blob, fileName } = await apiService.downloadDocument(documentId);
      // Same forced-download approach as handleDownloadAll below, not
      // window.open(blobUrl, '_blank') — mobile browsers (Android Chrome
      // especially) block/ignore that popup once the surrounding await has
      // broken the user-activation window.
      triggerDownload(blob, fileName);
    } catch (err: any) {
      setDocumentsError(err.message || 'Failed to open document');
    } finally {
      setViewingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!selectedUser) return;
    setDownloadingAll(true);
    setDocumentsError('');
    try {
      const blob = await apiService.downloadMergedProfileDocumentsPdf(selectedUser.id);
      triggerDownload(blob, `${selectedUser.firstName}-${selectedUser.lastName}-Documentary-Requirements.pdf`);
    } catch (err: any) {
      setDocumentsError(err.message || 'Failed to download documents');
    } finally {
      setDownloadingAll(false);
    }
  };

  const filteredApplicants = applicants.filter((u) => {
    if (!userSearch) return false;
    const q = userSearch.toLowerCase();
    return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newType.trim()) return;
    setAdding(true);
    setError('');
    try {
      await apiService.createDocumentRequirement(newType.trim(), newIsRequired);
      setNewType('');
      setNewIsRequired(true);
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to add document requirement');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleRequired = async (requirement: DocumentRequirement, isRequired: boolean) => {
    setTogglingId(requirement.id);
    setError('');
    try {
      const updated = await apiService.setDocumentRequirementRequired(requirement.id, isRequired);
      setRequirements((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err: any) {
      setError(err.message || 'Failed to update document requirement');
    } finally {
      setTogglingId(null);
    }
  };

  const requiredCount = requirements.filter((r) => r.isRequired).length;

  const handleConfirmDelete = async () => {
    if (!deletingRequirement) return;
    setDeleting(true);
    setError('');
    try {
      await apiService.deleteDocumentRequirement(deletingRequirement.id);
      setDeletingRequirement(null);
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document requirement');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Document Requirements</div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Documentary Requirements</h1>
          <p>
            Every document type listed here appears in "VIII. Documentary Requirements" on every applicant's profile.
            Required ones must be uploaded before applying; optional ones can be uploaded but never hold an application
            back.{' '}
            {!loading && (
              <strong>
                Total: {requirements.length.toLocaleString()} ({requiredCount.toLocaleString()} required,{' '}
                {(requirements.length - requiredCount).toLocaleString()} optional)
              </strong>
            )}
          </p>
        </div>

        {error && (
          <div
            className="alert-error"
            style={{ padding: '1rem', marginBottom: '1.5rem' }}
          >
            {error}
          </div>
        )}

        {!isViewer && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0, minWidth: '280px', flex: 1 }}>
                <label htmlFor="newDocType">New document requirement</label>
                <input
                  id="newDocType"
                  placeholder="e.g. Barangay Clearance"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Applicants must upload it?</label>
                <div style={{ padding: '0.55rem 0' }}>
                  <ToggleSwitch
                    checked={newIsRequired}
                    onChange={setNewIsRequired}
                    disabled={adding}
                    label={newIsRequired ? 'Required' : 'Optional'}
                    ariaLabel="Required"
                  />
                </div>
              </div>
              <button className="btn btn-primary" type="submit" disabled={adding || !newType.trim()}>
                {adding ? 'Adding...' : 'Add Requirement'}
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : requirements.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>No document requirements yet — add one above.</p>
          </div>
        ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Document Type</th>
                  <th>Required / Optional</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {requirements.map((requirement, index) => (
                  <tr key={requirement.id}>
                    <td>{index + 1}</td>
                    <td>{requirement.documentType}</td>
                    <td>
                      {isViewer ? (
                        <span className={`badge ${requirement.isRequired ? 'badge-primary' : 'badge-secondary'}`}>
                          {requirement.isRequired ? 'Required' : 'Optional'}
                        </span>
                      ) : (
                        <ToggleSwitch
                          checked={requirement.isRequired}
                          onChange={(next) => handleToggleRequired(requirement, next)}
                          disabled={togglingId === requirement.id}
                          label={requirement.isRequired ? 'Required' : 'Optional'}
                          ariaLabel={`${requirement.documentType} required`}
                        />
                      )}
                    </td>
                    <td>{formatDate(requirement.createdAt)}</td>
                    <td>
                      {!isViewer && (
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                          onClick={() => setDeletingRequirement(requirement)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isViewer && (
          <>
            <div className="page-header" style={{ marginTop: '2.5rem' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span aria-hidden="true">📁</span> Documentary Requirements
              </h2>
              <p>Search a student to view and download everything they've uploaded to their profile.</p>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
                <label htmlFor="applicantSearch">Search Users</label>
                <input
                  id="applicantSearch"
                  placeholder="Student name or email"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>

              {userSearch && (
                <div style={{ marginTop: '0.75rem' }}>
                  {filteredApplicants.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No matching students.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {filteredApplicants.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="btn btn-outline btn-sm"
                          style={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            background: selectedUser?.id === u.id ? 'rgba(139, 92, 246, 0.1)' : undefined
                          }}
                          disabled={documentsLoading}
                          onClick={() => openUser(u)}
                        >
                          {u.firstName} {u.lastName} <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {selectedUser && (
          <div className="card">
            <div
              className="card-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}
            >
              <h3>
                {selectedUser.firstName} {selectedUser.lastName}'s Documents
              </h3>
              <button
                className="btn btn-primary btn-sm"
                disabled={downloadingAll || userDocuments.length === 0}
                onClick={handleDownloadAll}
              >
                {downloadingAll ? 'Preparing PDF...' : 'Download All as One PDF'}
              </button>
            </div>

            {documentsError && (
              <p style={{ color: 'var(--error-text)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{documentsError}</p>
            )}

            {documentsLoading ? (
              <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
            ) : userDocuments.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>This student hasn't uploaded any documents yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {userDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.85rem',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <div>
                      <div>{doc.documentType}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{doc.fileName}</div>
                    </div>
                    <button
                      className="btn btn-outline btn-sm"
                      disabled={viewingId === doc.id}
                      onClick={() => handleViewDocument(doc.id)}
                    >
                      {viewingId === doc.id ? 'Opening...' : 'View'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {deletingRequirement && (
        <Modal title="Delete Document Requirement" onClose={() => setDeletingRequirement(null)}>
          <p className="alert-error" style={{ padding: '0.85rem' }}
          >
            Remove <strong>{deletingRequirement.documentType}</strong> from the required documents list? Scholars will
            no longer be asked to upload it, and it will disappear from "VIII. Documentary Requirements" on their
            profile (any copy they already uploaded is not deleted).
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              style={{ background: 'var(--error-text)' }}
              disabled={deleting}
              onClick={handleConfirmDelete}
            >
              {deleting ? 'Deleting...' : 'Delete Requirement'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setDeletingRequirement(null)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DocumentRequirementsPage;
